<template>
    <div
        v-for="(step, stepIdx) in animatedSteps"
        :key="step.stepIndex"
        class="module-wrap"
        :id="`animated-step-${step.stepIndex}`"
        :class="getStepMarginClass(step, stepIdx)"
        :ref="el => setStepRef(el, step)"
    >
        <template v-if="step.visible">
            <!-- title/body -->
            <AnimatedTypingHtmlText
                v-if="step.type === 'title' || step.type === 'body'"
                :class="step.type === 'title' ? 'module-title h5' : 'module-body body-3--regular'"
                :html="step.html ?? ''"
                :isActive="props.showAllImmediately || currentStep === step.stepIndex"
                :showAllImmediately="props.showAllImmediately"
                :moduleName="step.module.ModuleName ?? undefined"
                :textType="step.type"
                @done="
                    () => {
                        handleStepDone(step.stepIndex);
                        if (!props.showAllImmediately) nextStep(step.stepIndex);
                    }
                "
                @sentenceDone="handleSentenceDone"
            />
            <!-- graph -->
            <template v-else-if="step.type === 'graph' && step.content">
                <div class="module-graph">
                    <component
                        :is="resolveGraphComponent(step.content.graphType)"
                        v-bind="getGraphProps(step.content) as any"
                    />
                    <AutoAdvance
                        v-if="!props.showAllImmediately && currentStep === step.stepIndex"
                        :step-index="step.stepIndex"
                        @auto="
                            idx => {
                                handleStepDone(idx);
                                nextStep(idx);
                            }
                        "
                    />
                </div>
            </template>
            <!-- custom (ex: one-line-summary) -->
            <template v-else-if="step.type === 'custom'">
                <div :class="step.customClass" v-html="step.html" />
                <AutoAdvance
                    v-if="!props.showAllImmediately && currentStep === step.stepIndex"
                    :step-index="step.stepIndex"
                    @auto="
                        idx => {
                            handleStepDone(idx);
                            nextStep(idx);
                        }
                    "
                />
            </template>
            <!-- reference-list (클릭 시 참고자료 바텀시트 표시) -->
            <template v-else-if="step.type === 'reference-list'">
                <ul class="reference-list body-3--regular dot-style-list">
                    <li
                        class="reference-item"
                        v-for="(referenceItem, refIdx) in step.module.ReferenceModuleInfoResDto?.ReferenceSchemaList"
                        :key="refIdx"
                        v-show="referenceItem.ReferenceTitle"
                        @click="props.handleReferenceItemClick(referenceItem)"
                    >
                        <p
                            class="reference-title"
                            v-if="referenceItem.ReferenceTitle"
                            v-dompurify-html="referenceItem.ReferenceTitle"
                        ></p>
                    </li>
                </ul>
                <AutoAdvance
                    v-if="!props.showAllImmediately && currentStep === step.stepIndex"
                    :step-index="step.stepIndex"
                    @auto="
                        idx => {
                            handleStepDone(idx);
                            nextStep(idx);
                        }
                    "
                />
            </template>
        </template>
        <!-- SummaryModule의 바디 다음에 ReportCautionText 표시 (0번째 모듈인 경우만) -->
        <ReportCautionText
            v-if="
                step.moduleIndex === 0 &&
                step.module.ModuleName === 'SummaryModule' &&
                step.type === 'body' &&
                isLastStepInModule(step, stepIdx) &&
                props.cautionText &&
                (props.showAllImmediately || isFirstModuleBodyCompleted)
            "
            :text="props.cautionText"
        />
    </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, computed, type ComponentPublicInstance, nextTick } from 'vue';
import type {
    TrendReportResDto,
    ModuleBodyResDto,
    ReferenceSchema,
    VarianceHistogramResDto,
    ClusterApproachGraph,
    VarianceGraphResDto,
    ClusterGraphMetaInfo,
    StackedBar,
    PanelGraph as PanelGraphType,
    BorderlineBarGraph,
    VarianceVarianceResDto,
    ClusterPannelGraph,
} from '@common/openapi-client/openapi';
import { useUserInteractionLog } from '../../composables/useUserInteractionLog';
import StackedBarGraph from '../graph/StackedBarGraph.vue';
import PanelGraph from '../graph/PanelGraph.vue';
import VelocityHistogramBox from '../graph/VelocityHistogramBox.vue';
import VarianceBox from '../graph/VarianceBox.vue';
import ClusterChangeInfos from '../graph/clusterModule/ClusterChangeInfos.vue';
import TotalReferenceModuleGraph from '../graph/TotalReferenceModuleGraph.vue';
import AnimatedTypingHtmlText from '../animation/AnimatedTypingHtmlText.vue';
import ClusterChangePolygon from '../graph/clusterModule/ClusterChangePolygon.vue';
import AutoAdvance from './AutoAdvance.vue';
import ReportCautionText from '../common/ReportCautionText.vue';

interface GraphContent {
    graphType:
        | 'StackedBarGraph'
        | 'PanelGraph'
        | 'TotalReferenceModuleGraph'
        | 'VelocityHistogramBox'
        | 'VarianceBox'
        | 'ClusterChangeInfos'
        | 'ClusterChangePolygon';
    data:
        | StackedBar[]
        | PanelGraphType
        | BorderlineBarGraph
        | VarianceHistogramResDto
        | VarianceVarianceResDto
        | ClusterPannelGraph
        | ClusterApproachGraph;
    index?: number;
    bodyComp?: string;
    isAdmin?: boolean;
    screenWidth?: number;
    month?: number;
    isForShare?: boolean;
}

interface StepItem {
    stepIndex: number;
    moduleIndex: number;
    type: 'section' | 'title' | 'body' | 'graph' | 'custom' | 'reference-list';
    module: ModuleBodyResDto; // 원본 모듈
    content?: GraphContent; // 필요에 따라 그래프 데이터 등
    html?: string;
    visible: boolean;
    customClass?: string;
}

const animatedSteps = ref<StepItem[]>([]);
const currentStep = ref(0);
const isAutoScrolling = ref(false);
let autoScrollAbortController: AbortController | null = null;
const completedSteps = ref(new Set<number>());
const allAnimationsDone = ref(false);

// Intersection Observer 관련
const stepRefs = ref<Map<number, HTMLElement>>(new Map());
let intersectionObserver: IntersectionObserver | null = null;

const props = defineProps<{
    computedStore: { computedModuleData: TrendReportResDto | null };
    screenWidth: number;
    isAdmin: boolean;
    handleFloatingBtnClick: () => void;
    isFloatingFixed: boolean;
    isHiddenFloatingBtn: boolean;
    showAllImmediately?: boolean; // 모든 요소를 애니메이션 없이 보여주는 옵션 (false 시 자동 스크롤 활성)
    isForShare?: boolean; // 공유 페이지 여부 (true인 경우 로그 호출 안함)
    handleReferenceItemClick: (referenceItem: ReferenceSchema) => void;
    makeVelocityGraphKey: (varianceGraphData: VarianceHistogramResDto) => string;
    makeClusterGraphKey: (clusterGraphData: ClusterApproachGraph) => string;
    month?: string | null;
    endDate?: string | null; // endDate 추가
    cautionText?: string | null; // CautionText 추가
}>();

const emit = defineEmits<{
    (e: 'all-done', userScrolled: boolean): void;
}>();

// 로그 관련
const { saveLog } = useUserInteractionLog(props.isAdmin);

watch([currentStep, () => props.showAllImmediately, animatedSteps], updateVisibility, { immediate: true });

// showAllImmediately 변경 시 Intersection Observer 재설정
watch(
    () => props.showAllImmediately,
    newValue => {
        if (newValue) {
            nextTick(() => {
                setupIntersectionObserver();
            });
        } else {
            if (intersectionObserver) {
                intersectionObserver.disconnect();
                intersectionObserver = null;
            }
        }
    },
);

function updateVisibility() {
    if (props.showAllImmediately) {
        animatedSteps.value.forEach(s => (s.visible = true));
        currentStep.value = animatedSteps.value.length;

        // 모두 완료로 처리
        completedSteps.value = new Set(animatedSteps.value.map(s => s.stepIndex));
        maybeEmitAllDone();
    } else {
        animatedSteps.value.forEach((step, idx) => {
            step.visible = idx < currentStep.value + 1;
        });
    }
}

const buildAnimatedSteps = () => {
    resetCompletion();
    userScrolled.value = false;

    const result: StepItem[] = [];
    const moduleList = props.computedStore.computedModuleData?.ModuleBodyResDtoList ?? [];
    let stepIndex = 0;

    moduleList.forEach((module: ModuleBodyResDto, moduleIndex: number) => {
        // 1. 타이틀
        let title = '';
        if (module.ModuleName === 'SummaryModule') title = module.Summary?.Title ?? '';
        if (module.ModuleName === 'CommentModule') title = module.CommentModuleInfoResDto?.Title ?? '';
        if (module.ModuleName === 'ReferenceModule') title = module.ReferenceModuleInfoResDto?.Title ?? '';
        if (module.ModuleName === 'VarianceModule') title = module.VarianceInfoResDto?.Title ?? '';
        if (module.ModuleName === 'EndingModule') title = module.EndingResDto?.Title ?? '';
        if (module.ModuleName === 'ClusterModule') title = module.ClusterModuleResDto?.Title ?? '';
        if (title) {
            result.push({
                stepIndex,
                moduleIndex,
                type: 'title',
                module,
                html: title,
                visible: false,
            });
            stepIndex++;
        }

        // 2. 그래프 (필요한 경우 여러 개)
        if (module.ModuleName === 'CommentModule' && module.CommentModuleInfoResDto?.StackedBarList) {
            result.push({
                stepIndex,
                moduleIndex,
                type: 'graph',
                module,
                content: { graphType: 'StackedBarGraph', data: module.CommentModuleInfoResDto.StackedBarList },
                visible: false,
            });
            stepIndex++;
        }
        if (module.ModuleName === 'CommentModule' && module.CommentModuleInfoResDto?.PanelGraph) {
            result.push({
                stepIndex,
                moduleIndex,
                type: 'graph',
                module,
                content: {
                    graphType: 'PanelGraph',
                    data: module.CommentModuleInfoResDto.PanelGraph,
                    isAdmin: props.isAdmin,
                },
                visible: false,
            });
            stepIndex++;
        }
        if (
            module.ModuleName === 'ReferenceModule' &&
            module.ReferenceModuleInfoResDto?.ReferenceSchemaList?.length &&
            module.ReferenceModuleInfoResDto?.ReferenceSchemaList?.length > 0
        ) {
            module.ReferenceModuleInfoResDto.ReferenceSchemaList?.forEach((schema: ReferenceSchema, i: number) => {
                if (schema.BorderlineBarGraph) {
                    result.push({
                        stepIndex,
                        moduleIndex,
                        type: 'graph',
                        module,
                        content: {
                            graphType: 'TotalReferenceModuleGraph',
                            data: schema.BorderlineBarGraph,
                            index: i,
                            bodyComp: schema.BodyComp ?? '',
                        },
                        visible: false,
                    });
                    stepIndex++;
                }
            });
        }
        if (
            module.ModuleName === 'VarianceModule' &&
            module.VarianceInfoResDto?.VarianceGraphResDtoList?.length &&
            module.VarianceInfoResDto?.VarianceGraphResDtoList?.length > 0
        ) {
            module.VarianceInfoResDto.VarianceGraphResDtoList?.forEach((graph: VarianceGraphResDto, i: number) => {
                if (graph.GraphType === 'Histogram' && graph.VarianceHistogramResDto) {
                    result.push({
                        stepIndex,
                        moduleIndex,
                        type: 'graph',
                        module,
                        content: {
                            graphType: 'VelocityHistogramBox',
                            data: graph.VarianceHistogramResDto,
                            index: i,
                            screenWidth: props.screenWidth,
                        },
                        visible: false,
                    });
                    stepIndex++;
                }
                if (graph.GraphType === 'Variance' && graph.VarianceVarianceResDto) {
                    result.push({
                        stepIndex,
                        moduleIndex,
                        type: 'graph',
                        module,
                        content: {
                            graphType: 'VarianceBox',
                            data: graph.VarianceVarianceResDto,
                            index: i,
                            isAdmin: props.isAdmin,
                            screenWidth: props.screenWidth,
                            isForShare: props.isForShare,
                        },
                        visible: false,
                    });
                    stepIndex++;
                }
            });
        }
        if (
            module.ModuleName === 'ClusterModule' &&
            module.ClusterModuleResDto?.GraphInfo?.length &&
            module.ClusterModuleResDto?.GraphInfo?.length > 0
        ) {
            module.ClusterModuleResDto.GraphInfo?.forEach((graph: ClusterGraphMetaInfo) => {
                // 1. 패널 그래프
                if (graph.GraphType === 'ClusterPannelGraph' && graph.ClusterPannelGraph) {
                    result.push({
                        stepIndex,
                        moduleIndex,
                        type: 'graph',
                        module,
                        content: { graphType: 'ClusterChangeInfos', data: graph.ClusterPannelGraph },
                        visible: false,
                    });
                    stepIndex++;
                }
                // 2. ApproachGraph
                if (graph.GraphType === 'ClusterApproachGraph' && graph.ClusterApproachGraph) {
                    const [_, monthStr] = props.month?.split('T')[0].split('-') ?? [];
                    let monthText = parseInt(monthStr, 10);

                    // 이전달 계산
                    if (monthText === 1) {
                        monthText = 12;
                    } else {
                        monthText -= 1;
                    }
                    // 👈 여기 key 맞춰야 한다!
                    result.push({
                        stepIndex,
                        moduleIndex,
                        type: 'graph',
                        module,
                        content: {
                            graphType: 'ClusterChangePolygon',
                            data: graph.ClusterApproachGraph,
                            screenWidth: props.screenWidth,
                            month: monthText,
                            isForShare: props.isForShare,
                        },
                        visible: false,
                    });
                    stepIndex++;
                }
            });
        }

        // 3. 바디
        let body = '';
        if (module.ModuleName === 'SummaryModule') body = module.Summary?.Body ?? '';
        if (module.ModuleName === 'CommentModule') body = module.CommentModuleInfoResDto?.Body ?? '';
        if (module.ModuleName === 'ReferenceModule') body = module.ReferenceModuleInfoResDto?.Body ?? '';
        if (module.ModuleName === 'VarianceModule') body = module.VarianceInfoResDto?.Body ?? '';
        if (module.ModuleName === 'EndingModule') body = module.EndingResDto?.Body ?? '';
        if (module.ModuleName === 'ClusterModule') body = module.ClusterModuleResDto?.Body ?? '';
        if (body) {
            result.push({
                stepIndex,
                moduleIndex,
                type: 'body',
                module,
                html: body,
                visible: false,
            });
            stepIndex++;
        }

        // 3-1. ReferenceModule 클릭 가능한 참고자료 목록 (클릭 시 바텀시트 표시)
        if (
            module.ModuleName === 'ReferenceModule' &&
            module.ReferenceModuleInfoResDto?.ReferenceSchemaList?.some(
                (schema: ReferenceSchema) => schema.ReferenceTitle,
            )
        ) {
            result.push({
                stepIndex,
                moduleIndex,
                type: 'reference-list',
                module,
                visible: false,
            });
            stepIndex++;
        }

        // 4. 부가요소(예: one-line-summary)
        // 주로 CommentModule 등에서만 쓰임. 필요에 따라 ReferenceModule 등도 확장 가능
        if (module.ModuleName === 'CommentModule' && module.CommentModuleInfoResDto?.FitscoreEval) {
            result.push({
                stepIndex,
                moduleIndex,
                type: 'custom',
                module,
                html: module.CommentModuleInfoResDto.FitscoreEval,
                visible: false,
                customClass: 'one-line-summary caption--regular',
            });
            stepIndex++;
        }
    });

    currentStep.value = 0;
    animatedSteps.value = result;

    // 첫번째 step 바로 visible로 설정
    if (animatedSteps.value.length > 0) {
        animatedSteps.value[0].visible = true;
    }

    // showAllImmediately가 true일 때 Intersection Observer 설정
    if (props.showAllImmediately) {
        // DOM 업데이트 후 observer 설정을 위해 nextTick 사용
        nextTick(() => {
            setupIntersectionObserver();
        });
    }
};

const resolveGraphComponent = (graphType: string) => {
    switch (graphType) {
        case 'StackedBarGraph':
            return StackedBarGraph;
        case 'PanelGraph':
            return PanelGraph;
        case 'TotalReferenceModuleGraph':
            return TotalReferenceModuleGraph;
        case 'VelocityHistogramBox':
            return VelocityHistogramBox;
        case 'VarianceBox':
            return VarianceBox;
        case 'ClusterChangeInfos':
            return ClusterChangeInfos;
        case 'ClusterChangePolygon':
            return ClusterChangePolygon;
        default:
            return null;
    }
};

const getGraphProps = (content: GraphContent) => {
    const { graphType: _graphType, ...props } = content;
    return props as Record<string, unknown>;
};

const getStepMarginClass = (step: StepItem, stepIdx: number) => {
    // 마지막 요소이거나, 다음 step이 다른 moduleIndex면(즉, 섹션 마지막)
    const next = animatedSteps.value[stepIdx + 1];
    if (!next || next.moduleIndex !== step.moduleIndex) {
        return 'big-margin'; // 섹션 마지막 하단 마진
    }
    return 'small-margin'; // 같은 섹션 내 마진
};

const isLastStepInModule = (step: StepItem, stepIdx: number) => {
    // 다음 step이 없거나 다른 moduleIndex면 마지막 step
    const next = animatedSteps.value[stepIdx + 1];
    return !next || next.moduleIndex !== step.moduleIndex;
};

// 0번째 모듈의 바디 step이 완료되었는지 확인
const isFirstModuleBodyCompleted = computed(() => {
    if (props.showAllImmediately) return true;

    const firstModule = props.computedStore.computedModuleData?.ModuleBodyResDtoList?.[0];
    if (firstModule?.ModuleName !== 'SummaryModule') return false;

    // 0번째 모듈의 바디 step 찾기
    const firstModuleBodyStep = animatedSteps.value.find(
        step => step.moduleIndex === 0 && step.type === 'body' && step.module.ModuleName === 'SummaryModule',
    );

    if (!firstModuleBodyStep) return false;

    // 해당 step이 완료되었는지 확인
    return completedSteps.value.has(firstModuleBodyStep.stepIndex);
});

const nextStep = (stepIndex: number) => {
    if (currentStep.value === stepIndex) {
        currentStep.value++;

        // showAllImmediately 옵션이면 스크롤 자체를 안함
        if (props.showAllImmediately) return;

        // 사용자가 스크롤했으면 자동 스크롤만 스킵
        if (userScrolled.value) {
            return;
        }

        isAutoScrolling.value = true;

        // 기존 이벤트 리스너 정리
        removeUserScrollListeners();
        addUserScrollListeners();

        if (autoScrollAbortController) {
            autoScrollAbortController.abort();
        }
        autoScrollAbortController = new AbortController();
        const { signal } = autoScrollAbortController;

        setTimeout(() => {
            const nextEl = document.getElementById(`animated-step-${currentStep.value}`);
            if (nextEl && !signal.aborted) {
                nextEl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                setTimeout(() => {
                    isAutoScrolling.value = false;
                    removeUserScrollListeners();
                }, 800);
            }
        }, 100);
    }
};

const userScrolled = ref(false);

// Intersection Observer 관련 함수들
const setStepRef = (el: Element | ComponentPublicInstance | null, step: StepItem) => {
    if (el && 'id' in el) {
        stepRefs.value.set(step.stepIndex, el as HTMLElement);
    } else {
        stepRefs.value.delete(step.stepIndex);
    }
};

const setupIntersectionObserver = () => {
    // if (!props.showAllImmediately) return;

    // 기존 observer 정리
    if (intersectionObserver) {
        intersectionObserver.disconnect();
    }

    intersectionObserver = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const stepIndex = parseInt(entry.target.id.replace('animated-step-', ''));
                    const step = animatedSteps.value.find(s => s.stepIndex === stepIndex);

                    if (step && step.type === 'title' && !props.isForShare) {
                        const endDateStr = props.month ? props.month.split('T')[0] : '';
                        saveLog(161, 'EXPOSE_REPORT_MODULE', `${endDateStr},${step.module.ModuleName}`);
                    }
                }
            });
        },
        {
            rootMargin: '0px 0px -30px 0px', // viewport 하단에서 50px 위에서 트리거
            threshold: 0.1,
        },
    );

    // title 타입의 step들만 observe
    stepRefs.value.forEach((el, stepIndex) => {
        const step = animatedSteps.value.find(s => s.stepIndex === stepIndex);
        if (step && step.type === 'title') {
            intersectionObserver?.observe(el);
        }
    });
};

function onUserScroll(e: Event) {
    // wheel 또는 touchmove만 사용자 액션으로 인정
    if (e.type === 'wheel' || e.type === 'touchmove') {
        userScrolled.value = true;
        isAutoScrolling.value = false;
        if (autoScrollAbortController) {
            autoScrollAbortController.abort();
        }
        removeUserScrollListeners();
    }
}

function addUserScrollListeners() {
    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchmove', onUserScroll, { passive: true });
}
function removeUserScrollListeners() {
    window.removeEventListener('wheel', onUserScroll);
    window.removeEventListener('touchmove', onUserScroll);
}

const handleSentenceDone = (_sentenceIdx: number, sentenceEl: HTMLElement) => {
    if (sentenceEl?.scrollIntoView) {
        sentenceEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

function resetCompletion() {
    completedSteps.value.clear();
    allAnimationsDone.value = false;
}

function maybeEmitAllDone() {
    if (
        !allAnimationsDone.value &&
        animatedSteps.value.length > 0 &&
        completedSteps.value.size === animatedSteps.value.length
    ) {
        allAnimationsDone.value = true;
        if (!props.showAllImmediately) {
            emit('all-done', userScrolled.value);
        }

        userScrolled.value = false;
    }
}

const handleStepDone = (stepIndex: number) => {
    completedSteps.value.add(stepIndex);
    maybeEmitAllDone();
};

onMounted(buildAnimatedSteps);

onUnmounted(() => {
    removeUserScrollListeners();
    if (autoScrollAbortController) {
        autoScrollAbortController.abort();
    }
    if (intersectionObserver) {
        intersectionObserver.disconnect();
    }
});
</script>

<style scoped lang="scss">
.module-wrap {
    padding: 0 1.5rem;

    &.big-margin {
        margin-bottom: 6rem;
    }

    &.small-margin {
        margin-bottom: 1rem;
    }
}

.title {
    display: inline-block;
    margin-bottom: 1rem;
    white-space: pre-line;
    word-break: keep-all;
}

.one-line-summary {
    background-color: $grey-1;
    padding: 1rem 1.5rem;
    border-radius: 1rem;
    margin-top: 1rem;
}

.reference-list {
    display: inline-flex;
    flex-direction: column;
    padding-top: 4rem;

    li {
        color: $grey-7;
        text-decoration: underline;
        display: inline-block;
        cursor: pointer;

        & + li {
            margin-top: 0.5rem;
        }
    }
}

hr {
    margin: 0;
    background: $grey-3;
    height: 1px;
    border: 0;
    padding: 0 1.5rem;
}
</style>
